import {
  IsOptional,
  IsInt,
  Min,
  IsString,
  IsDateString,
} from 'class-validator';
import { ApiPropertyOptional } from '@nestjs/swagger';
import { Type } from 'class-transformer';

/**
 * DTO para filtrar la búsqueda de Control DUS.
 * Permite filtrar por cliente, nave, rango de fechas, contenedor, DUS, booking, referencia cliente, etapa y estado.
 *
 * @remarks
 * - Los clientes solo pueden ver sus propios DUS (clienteId se fuerza automáticamente por el backend)
 * - Los administradores pueden filtrar por cualquier cliente
 * - Todos los filtros son opcionales excepto paginación
 *
 * @example
 * ```typescript
 * const filters: ControlDusFilterDto = {
 *   clienteId: 66,
 *   nave: 'MSC SANTA MARIA',
 *   dateFrom: '2025-01-01',
 *   dateTo: '2025-01-31',
 *   dus: '13009996',
 *   page: 1,
 *   limit: 10,
 *   orderBy: 'etd',
 *   orderDirection: 'DESC'
 * };
 * ```
 */
export class ControlDusFilterDto {
  /**
   * ID del cliente a filtrar. Si no se especifica, depende del rol del usuario.
   * - Cliente: solo puede ver su propio ID (se fuerza automáticamente)
   * - Admin/Superuser: puede seleccionar cualquier cliente
   *
   * @example 66
   */
  @ApiPropertyOptional({
    description: 'ID del cliente',
    example: 66,
    type: Number,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  clienteId?: number;

  /**
   * ID del consignatario (ConsigneeId en tblParties/MSSQL).
   * Lo asigna el backend automáticamente cuando el rol es 'consignatario'.
   *
   * @example 42
   */
  @ApiPropertyOptional({
    description: 'ID del consignatario (asignado automáticamente por backend)',
    example: 42,
    type: Number,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  consigneeId?: number;

  /**
   * ID de la temporada a filtrar.
   * Filtra por rango de fechas de la temporada (SeasonFrom - SeasonTo).
   *
   * @example 7
   */
  @ApiPropertyOptional({
    description: 'ID de la temporada',
    example: 7,
    type: Number,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  temporadaId?: number;

  /**
   * Nombre de la nave a filtrar (búsqueda parcial con LIKE)
   *
   * @example 'MSC SANTA MARIA'
   */
  @ApiPropertyOptional({
    description: 'Nombre de la nave',
    example: 'MSC SANTA MARIA',
  })
  @IsOptional()
  @IsString()
  nave?: string;

  /**
   * Fecha de inicio del rango a consultar (YYYY-MM-DD).
   * Se aplica sobre el campo ETDDate de la vista.
   *
   * @example '2025-01-01'
   */
  @ApiPropertyOptional({
    description: 'Fecha de inicio del rango (YYYY-MM-DD)',
    example: '2025-01-01',
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  /**
   * Fecha de fin del rango a consultar (YYYY-MM-DD).
   * Se aplica sobre el campo ETDDate de la vista.
   *
   * @example '2025-01-31'
   */
  @ApiPropertyOptional({
    description: 'Fecha de fin del rango (YYYY-MM-DD)',
    example: '2025-01-31',
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;

  /**
   * Referencia del cliente para filtrar (búsqueda parcial con LIKE)
   *
   * @example '192'
   */
  @ApiPropertyOptional({
    description: 'Referencia del cliente',
    example: '192',
  })
  @IsOptional()
  @IsString()
  referenciaCliente?: string;

  /**
   * Número de contenedor para filtrar (búsqueda parcial con LIKE)
   *
   * @example 'MSNU8697170'
   */
  @ApiPropertyOptional({
    description: 'Número de contenedor',
    example: 'MSNU8697170',
  })
  @IsOptional()
  @IsString()
  contenedor?: string;

  /**
   * Número de DUS para filtrar (búsqueda parcial con LIKE)
   *
   * @example '13009996-3'
   */
  @ApiPropertyOptional({
    description: 'Número de DUS',
    example: '13009996-3',
  })
  @IsOptional()
  @IsString()
  dus?: string;

  /**
   * Número de booking/reserva para filtrar (búsqueda parcial con LIKE)
   *
   * @example '070ISA1205214'
   */
  @ApiPropertyOptional({
    description: 'Número de booking/reserva',
    example: '070ISA1205214',
  })
  @IsOptional()
  @IsString()
  booking?: string;

  /**
   * Etapa del proceso para filtrar
   * Valores posibles: 'Documentación', 'Legalización', 'Inicial'
   *
   * @example 'Legalización'
   */
  @ApiPropertyOptional({
    description: 'Etapa del proceso',
    example: 'Legalización',
    enum: ['Documentación', 'Legalización', 'Inicial'],
  })
  @IsOptional()
  @IsString()
  etapa?: string;

  /**
   * Estado actual del DUS para filtrar
   * Valores posibles: 'Pendiente', 'En Proceso', 'Finalizado'
   *
   * @example 'Pendiente'
   */
  @ApiPropertyOptional({
    description: 'Estado actual del DUS',
    example: 'Pendiente',
    enum: ['Pendiente', 'En Proceso', 'Finalizado'],
  })
  @IsOptional()
  @IsString()
  estado?: string;

  /**
   * Si es true, excluye DUS con estado 'Cancelado' de los resultados.
   *
   * @example true
   */
  @ApiPropertyOptional({
    description: 'Excluir DUS cancelados de los resultados',
    example: true,
    type: Boolean,
  })
  @IsOptional()
  @Type(() => Boolean)
  excludeCancelado?: boolean;

  /**
   * Página de resultados (paginación server-side).
   *
   * @default 1
   * @minimum 1
   */
  @ApiPropertyOptional({
    description: 'Número de página',
    type: Number,
    example: 1,
    default: 1,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number = 1;

  /**
   * Cantidad de resultados por página.
   *
   * @default 10
   * @minimum 1
   */
  @ApiPropertyOptional({
    description: 'Elementos por página',
    type: Number,
    example: 10,
    default: 10,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  limit?: number = 10;

  /**
   * Columna por la que se ordena la consulta.
   * Opciones disponibles: 'etd', 'cliente', 'estado', 'etapa', 'dus', 'nave'
   *
   * @default 'etd'
   */
  @ApiPropertyOptional({
    description:
      "Columna para ordenar (ej: 'etd', 'cliente', 'estado', 'etapa', 'dus', 'nave')",
    example: 'etd',
    default: 'etd',
  })
  @IsOptional()
  @IsString()
  orderBy?: string = 'etd';

  /**
   * Dirección de ordenamiento.
   *
   * @default 'DESC'
   */
  @ApiPropertyOptional({
    description: "Dirección de orden: 'ASC' o 'DESC'",
    enum: ['ASC', 'DESC'],
    example: 'DESC',
    default: 'DESC',
  })
  @IsOptional()
  @IsString()
  orderDirection?: 'ASC' | 'DESC' = 'DESC';
}
